import type { AutopilotCommand } from '../autopilot_operation/autopilotCommand.model';
import type { AutopilotTrajectory } from '../autopilot_operation/autopilotTrajectory.model';
import type { Directory, PlanningItem } from '../general/directoryTree.model';
import type { LinkedList } from '../general/linked-list.model';
import type { Region } from '../region/region.model';
import { AutopilotCommandEnum, AutopilotSpeedTypeEnum, AutopilotSpeedUnitsEnum } from '../autopilot_operation';
import { AutopilotAltitudeTypeEnum, AutopilotAltitudeUnitsEnum } from '../autopilot_operation/autopilotAltitude.enums';
import type { TrajectoryState } from './trajectoryState.enum';
import type { WGS84Centroid } from '../geospatial';

export class TrajectoryUtils {

    /**
     * Method to obtain the generic waypoint after clicking the map. It will create the waypoint with the default values and the coordinates of the click.
     * @param latitude: latitude coordinates of the click on the map.
     * @param longitude: longitude coordinates of the click on the map.
     * @param altitude: altitude in meters
     * @param speed: speed in m/s
     * @param label: label of the waypoint to be created (normally is the AutopilotCommandEnum value of the waypoint type, but can be different if needed)
     * @param state: trajectory creation state
     * @param stage: trajectory stage
     * @param waypointType: type of waypoint
     * @param branch: branch label
     * @param reg: optional region
     * @returns: generated waypoint in AutopilotCommand format.
    */
    obtainAPCommand(
        latitude: number,
        longitude: number,
        altitude: number,
        speed: number,
        label: string,
        state: TrajectoryState,
        stage: string,
        waypointType: AutopilotCommandEnum,
        branch: string,
        reg?: Region
    ): AutopilotCommand {
        const apCommand: AutopilotCommand = {
            stateTrajectory: state,
            stageLabel: stage,
            apCommand: waypointType,
            branchLabel: branch,
            label: label,
            speed: parseFloat(speed.toFixed(2)),
            speedType: AutopilotSpeedTypeEnum.Ground,
            speedUnits: AutopilotSpeedUnitsEnum.mps,
            point: {
                latitude: parseFloat(latitude.toFixed(7)),
                longitude: parseFloat(longitude.toFixed(7)),
                altitude: parseFloat(altitude.toFixed(2)),
                altitudeType: AutopilotAltitudeTypeEnum.agl,
                altitudeUnits: AutopilotAltitudeUnitsEnum.meters
            },
            nextCommandIdx: 0,
            nextCommandIdxList: [],
            nextCommandLabel: '',
            nextCommandLabelList: [],
            prevCommandIdx: 0,
            prevCommandIdxList: [],
            index: 0,
            yaw: 0,
            time: 0,
            timeUnits: 0,
            distance: 0,
            distanceUnits: 0,
            armMode: 0,
            stateMode: 0,
            regionUUID: '',
            region: {} as Region,
            forkLabel: ''
        } as AutopilotCommand;

        if (reg) {
            apCommand.region = reg;
            apCommand.regionUUID = reg.uniqueId;
        }

        return apCommand;
    }

    /**
     * Check if a point is inside a region defined by a polygon
     * @param point Point to check with lat/lng properties
     * @param rset Array of points defining the polygon boundary
     * @returns true if point is inside the region
     */
    isInRegion(point: { lat: number; lng: number }, rset: Array<{ lat: number; lng: number }>): boolean {
        let result: boolean = false;
        let j: number = rset.length - 1;

        for (let i = 0; i < rset.length; i++) {
            const currentPoint = rset[i];
            const prevPoint = rset[j];
            if (!currentPoint || !prevPoint) continue;
            
            if (currentPoint.lat < point.lat && prevPoint.lat >= point.lat || prevPoint.lat < point.lat && currentPoint.lat >= point.lat) {
                if (currentPoint.lng + (point.lat - currentPoint.lat) / (prevPoint.lat - currentPoint.lat) * (prevPoint.lng - currentPoint.lng) < point.lng) {
                    result = !result;
                }
            }
            j = i;
        }
        return result;
    }

    countInitials(tree: LinkedList<Directory>): number {
        const initials: Array<string> = [];
        tree.obtainArray().forEach((item: Directory) => {
            item.items?.forEach((subItem: PlanningItem) => {
                if (subItem.branchLabel && subItem.branchLabel.includes('Initial')) {
                    if (!initials.includes(subItem.branchLabel)) {
                        initials.push(subItem.branchLabel);
                    }
                }
            });
        });
        return initials.length;
    }

    countFinals(tree: LinkedList<Directory>): number {
        const finals: Array<string> = [];
        tree.obtainArray().forEach((item: Directory) => {
            item.items?.forEach((subItem: PlanningItem) => {
                if (subItem.branchLabel && subItem.branchLabel.includes('Arrival')) {
                    if (!finals.includes(subItem.branchLabel)) {
                        finals.push(subItem.branchLabel);
                    }
                }
            });
        });
        return finals.length;
    }

    obtainLastMissionWaypoint(tree: LinkedList<Directory>): AutopilotCommand | undefined {
        const array: Array<AutopilotCommand> = [];
        tree.obtainArray().forEach((item: Directory) => {
            item.items?.forEach((subItem: PlanningItem) => {
                if (subItem.stageLabel === 'Mission') {
                    array.push(subItem as AutopilotCommand);
                }
            });
        });
        return array[array.length - 1];
    }

    obtainFirstMissionWaypoint(tree: LinkedList<Directory>): AutopilotCommand | undefined {
        const array: Array<AutopilotCommand> = [];
        tree.obtainArray().forEach((item: Directory) => {
            item.items?.forEach((subItem: PlanningItem) => {
                if (subItem.stageLabel === 'Mission') {
                    array.push(subItem as AutopilotCommand);
                }
            });
        });
        return array[0];
    }

    private toAutopilotCommand(item: PlanningItem, index: number): AutopilotCommand {
        return {
            apCommand: item.apCommand!,
            branchLabel: item.branchLabel ?? '',
            stageLabel: item.stageLabel ?? '',
            label: item.label ?? '',              // or whatever field you use
            index,
            point: item.point ? structuredClone(item.point) : (null as any),
            speed: item.speed ?? 0,
            speedType: item.speedType ?? 0,
            speedUnits: item.speedUnits ?? 0,
            stateTrajectory: item.stateTrajectory ?? 0,
            nextCommandIdx: 0,
            nextCommandIdxList: [],
            nextCommandLabel: '',
            nextCommandLabelList: [],
            prevCommandIdx: 0,
            prevCommandIdxList: [],
            forkLabel: item.forkLabel ?? '',
            stateMode: item.stateMode ?? 0,
            time: item.time ?? 0,
            timeUnits: item.timeUnits ?? 0,
            yaw: item.yaw ?? 0,
            distance: item.distance ?? 0,
            distanceUnits: item.distanceUnits ?? 0,
            armMode: item.armMode ?? 0,
            regionUUID: item.regionUUID ?? '',
            region: item.region ? structuredClone(item.region) : ({} as Region),
        };
    }


    createCommandListFromTree(tree: LinkedList<Directory>): Array<AutopilotCommand> {
        const allCommandList: Array<AutopilotCommand> = [];
        let waypointCounter = 0;

        const firstMission = this.obtainFirstMissionWaypoint(tree);
        const lastMission  = this.obtainLastMissionWaypoint(tree);

        tree.obtainArray().forEach((dir: Directory) => {
            dir.items?.forEach((subItem: PlanningItem) => {

            // Case 1: this PlanningItem is a command
            if (subItem.apCommand) {

                // Add fictitious JoinRoute BEFORE first mission (if needed)
                if (subItem === firstMission && this.countInitials(tree) > 1) {
                const waypoint: AutopilotCommand = {
                    apCommand: AutopilotCommandEnum.JoinRoute,
                    branchLabel: subItem.branchLabel ?? '',
                    stageLabel: subItem.stageLabel ?? '',
                    label: 'JoinRouteFicticious',
                    index: waypointCounter++,
                    point: subItem.point!,
                    speed: subItem.speed ?? 0,
                    speedType: subItem.speedType ?? 0,
                    speedUnits: subItem.speedUnits ?? 0,
                    stateTrajectory: subItem.stateTrajectory ?? 0,
                    nextCommandIdx: 0,
                    nextCommandIdxList: [],
                    nextCommandLabel: '',
                    nextCommandLabelList: [],
                    prevCommandIdx: 0,
                    prevCommandIdxList: [],
                    forkLabel: subItem.forkLabel ?? '',
                    stateMode: subItem.stateMode ?? 0,
                    time: subItem.time ?? 0,
                    timeUnits: subItem.timeUnits ?? 0,
                    yaw: subItem.yaw ?? 0,
                    distance: subItem.distance ?? 0,
                    distanceUnits: subItem.distanceUnits ?? 0,
                    armMode: subItem.armMode ?? 0,
                    regionUUID: '',
                    region: {} as Region,
                };
                allCommandList.push(waypoint);
                }

                // Push COPY of the real command (no reference to tree object)
                allCommandList.push(this.toAutopilotCommand(subItem, waypointCounter++));

                // Add fictitious ForkRoute AFTER last mission (if needed)
                if (subItem === lastMission && this.countFinals(tree) > 1) {
                const waypoint: AutopilotCommand = {
                    apCommand: AutopilotCommandEnum.ForkRoute,
                    branchLabel: subItem.branchLabel ?? '',
                    stageLabel: subItem.stageLabel ?? '',
                    label: 'ForkRouteFicticious',
                    index: waypointCounter++,
                    point: subItem.point!,
                    speed: subItem.speed ?? 0,
                    speedType: subItem.speedType ?? 0,
                    speedUnits: subItem.speedUnits ?? 0,
                    stateTrajectory: subItem.stateTrajectory ?? 0,
                    nextCommandIdx: 0,
                    nextCommandIdxList: [],
                    nextCommandLabel: '',
                    nextCommandLabelList: [],
                    prevCommandIdx: 0,
                    prevCommandIdxList: [],
                    forkLabel: subItem.forkLabel ?? '',
                    stateMode: subItem.stateMode ?? 0,
                    time: subItem.time ?? 0,
                    timeUnits: subItem.timeUnits ?? 0,
                    yaw: subItem.yaw ?? 0,
                    distance: subItem.distance ?? 0,
                    distanceUnits: subItem.distanceUnits ?? 0,
                    armMode: subItem.armMode ?? 0,
                    regionUUID: '',
                    region: {} as Region,
                };
                allCommandList.push(waypoint);
                }

                return;
            }

            // Case 2: nested items
            if (subItem.items) {
                subItem.items.forEach((subSubItem: PlanningItem) => {
                if (subSubItem.apCommand) {
                    allCommandList.push(this.toAutopilotCommand(subSubItem, waypointCounter++));
                }
                });
            }
            });
        });

        return allCommandList;
    }

    /**
     * Obtain the centroid of a trajectory
     * @param tree Tree structure containing the trajectory
     * @returns Centroid point with latitude, longitude and altitude
     */
    obtainTrajectoryCentroid(tree: LinkedList<Directory>): WGS84Centroid {
        let nPoint = 0;
        let latCentroid = 0;
        let lonCentroid = 0;
        let accLon = 0, accLat = 0;

        this.createCommandListFromTree(tree).forEach((apCommand: AutopilotCommand) => {
            if (apCommand.point != null) {
                nPoint++;
                accLat += apCommand.point.latitude;
                accLon += apCommand.point.longitude;
            }
        });

        latCentroid = accLat / nPoint;
        lonCentroid = accLon / nPoint;

        const centroid: WGS84Centroid = {
            latitude: latCentroid,
            longitude: lonCentroid,
            altitude: 0,
            az: 0,
            radious: 0
        };

        return centroid;
    }

    applyAutopilotTrajectoryLogic(apTrajectory: AutopilotTrajectory): AutopilotTrajectory {
        // Check all labels of ApCommands (none can be repeated)
        // Add to nextCommandLabelLists the first waypoint of each fork for a ForkRoute ApCommand
        // Add to prevCommandList the last waypoint of each fork for a JoinRoute ApCommand
        // Add the nextCommandIdx and nextCommandLabel to all ApCommands
        // Add all takeoffs to the initialsLabelList
        // Add all commands to the labelMap
        let takeoffCounter: number = 0;
        let waypointCounter: number = 0;
        let forkRouteCounter: number = 0;
        let joinRouteCounter: number = 0;
        let missionAreaCounter: number = 0;
        let landCounter: number = 0;

        const forks: Map<string, AutopilotCommand> = new Map<string, AutopilotCommand>();
        let currentForkRoute: AutopilotCommand | undefined;

        let currentInitial: string | undefined;
        let currentArrival: string | undefined;

        // Set unique labels to all the waypoints and add all waypoints to the labelMap
        apTrajectory.allCommandList?.forEach((apCommand: AutopilotCommand) => {
            waypointCounter++;

            switch (apCommand.apCommand) {
                case 'Takeoff': {
                    apCommand.label = 'Takeoff ' + takeoffCounter++;
                    break;
                }
                case 'SetWaypoint': {
                    apCommand.label = 'Waypoint ' + waypointCounter;
                    break;
                }
                case 'ForkRoute': {
                    if (apCommand.label !== 'ForkRouteFicticious')
                        apCommand.label = 'ForkRoute ' + forkRouteCounter++;
                    break;
                }
                case 'JoinRoute': {
                    if (apCommand.label !== 'JoinRouteFicticious')
                        apCommand.label = 'JoinRoute ' + joinRouteCounter++;
                    break;
                }
                case 'MissionArea': {
                    apCommand.label = 'MissionArea ' + missionAreaCounter++;
                    break;
                }
                case 'Land': {
                    apCommand.label = 'Land ' + landCounter++;
                    break;
                }
            }
            if (apCommand.label && apTrajectory.labelMap) {
                apTrajectory.labelMap.set(apCommand.label, apCommand);
            }
        });

        // Add all nextCommandIdx and nextCommandLabel to all ApCommands
        apTrajectory.allCommandList?.forEach((apCommand: AutopilotCommand) => {
            waypointCounter++;
            const nextCommand = apTrajectory.allCommandList![apCommand.index! + 1];
            let prevCommand;
            if (apCommand.index! > 0)
                prevCommand = apTrajectory.allCommandList![apCommand.index! - 1];

            currentInitial = apCommand.branchLabel?.includes('Initial') ? apCommand.branchLabel : undefined;
            currentArrival = apCommand.branchLabel?.includes('Arrival') ? apCommand.branchLabel : undefined;

            switch (apCommand.apCommand) {
                case 'Takeoff': {
                    apTrajectory.initialLabelList?.push(apCommand.label!);
                    if (nextCommand && nextCommand.branchLabel === currentInitial) {
                        apCommand.nextCommandIdx = nextCommand.index!;
                        apCommand.nextCommandLabel = nextCommand.label!;
                    } else if ((nextCommand?.branchLabel && nextCommand.branchLabel !== currentInitial) || nextCommand?.stageLabel === 'Mission') {
                        apCommand.stageLabel = 'LastWaypointInitial';
                        apCommand.nextCommandIdx = nextCommand.index!;
                        apCommand.nextCommandLabel = nextCommand.label!;
                    } 
                    break;
                }
                case 'SetWaypoint': {
                    if (!apCommand.forkLabel) {
                        switch (apCommand.stageLabel) {
                            case 'Departure': {
                                if (nextCommand && nextCommand.branchLabel === currentInitial) {
                                    apCommand.nextCommandIdx = nextCommand.index!;
                                    apCommand.nextCommandLabel = nextCommand.label!;
                                } else if ((nextCommand && nextCommand.branchLabel && nextCommand.branchLabel !== currentInitial) || (nextCommand && nextCommand.stageLabel === 'Mission')) {
                                    apCommand.stageLabel = 'LastWaypointInitial';
                                }
                                break;
                            }
                            case 'Mission': {
                                if (nextCommand && nextCommand.stageLabel === 'Mission') {
                                    apCommand.nextCommandIdx = nextCommand.index!;
                                    apCommand.nextCommandLabel = nextCommand.label!;
                                } else if(nextCommand && nextCommand.stageLabel === 'Arrival') {
                                    apCommand.nextCommandIdx = nextCommand.index!;
                                    apCommand.nextCommandLabel = nextCommand.label!;
                                }
                                break;
                            }
                            case 'Arrival': {
                                if (prevCommand && (prevCommand.stageLabel === 'Mission' || (prevCommand.branchLabel && prevCommand.branchLabel !== currentArrival))) {
                                    apCommand.stageLabel = 'FirstWaypointArrival';
                                }
                                if (nextCommand && nextCommand.branchLabel === currentArrival) {
                                    apCommand.nextCommandIdx = nextCommand.index!;
                                    apCommand.nextCommandLabel = nextCommand.label!;
                                }
                                break;
                            }
                        }
                    } else {
                        if (!forks.has(apCommand.forkLabel)) {
                            forks.set(apCommand.forkLabel, apCommand);
                            const forkRoute = apTrajectory.allCommandList!.find((waypoint: AutopilotCommand) => waypoint === currentForkRoute);
                            if (forkRoute) {
                                forkRoute.nextCommandIdxList?.push(apCommand.index!);
                                forkRoute.nextCommandLabelList?.push(apCommand.label!);
                            }
                        }
                        if (nextCommand?.forkLabel === apCommand.forkLabel) {
                            apCommand.nextCommandIdx = nextCommand.index!;
                            apCommand.nextCommandLabel = nextCommand.label!;
                        } else if (nextCommand?.forkLabel && nextCommand.forkLabel !== apCommand.forkLabel) {
                            apCommand.stageLabel = 'NoJoin';
                        } else if (nextCommand?.apCommand === 'JoinRoute') {
                            apCommand.nextCommandIdx = nextCommand.index!;
                            apCommand.nextCommandLabel = nextCommand.label!;
                            nextCommand.prevCommandIdxList?.push(apCommand.index!);
                        }
                    }
                    break;
                }
                case 'ForkRoute': {
                    if (apCommand.label !== 'ForkRouteFicticious') {
                        currentForkRoute = apCommand;
                        currentForkRoute.nextCommandIdxList = [];
                        currentForkRoute.nextCommandLabelList = [];
                    } else {
                        apCommand.nextCommandIdxList = [];
                        apCommand.nextCommandLabelList = [];
                    }
                    break;
                }
                case 'JoinRoute': {
                    if (apCommand.label !== 'JoinRouteFicticious') {
                        currentForkRoute = undefined;
                        apCommand.prevCommandIdxList = [];
                        apTrajectory.allCommandList?.forEach((waypoint: AutopilotCommand) => {
                            if (waypoint.stageLabel === 'NoJoin') {
                                waypoint.nextCommandIdx = apCommand.index!;
                                waypoint.nextCommandLabel = apCommand.label!;
                                waypoint.stageLabel = 'Join added';
                                apCommand.prevCommandIdxList?.push(waypoint.index!);
                            }
                        });
                        switch (apCommand.stageLabel) {
                            case 'Departure': {
                                if (nextCommand && nextCommand.branchLabel === currentInitial) {
                                    apCommand.nextCommandIdx = nextCommand.index!;
                                    apCommand.nextCommandLabel = nextCommand.label!;
                                } else if (nextCommand && nextCommand.branchLabel && nextCommand.branchLabel !== currentInitial) {
                                    apCommand.stageLabel = 'LastWaypointInitial';
                                }
                                break;
                            }
                            case 'Mission': {
                                if (nextCommand && nextCommand.stageLabel === 'Mission') {
                                    apCommand.nextCommandIdx = nextCommand.index!;
                                    apCommand.nextCommandLabel = nextCommand.label!;
                                }
                                break;
                            }
                            case 'Arrival': {
                                if (prevCommand?.stageLabel === 'Mission' || (prevCommand?.branchLabel && prevCommand.branchLabel !== currentArrival)) {
                                    apCommand.stageLabel = 'FirstWaypointArrival';
                                }
                                if (nextCommand && nextCommand.branchLabel === currentArrival) {
                                    apCommand.nextCommandIdx = nextCommand.index!;
                                    apCommand.nextCommandLabel = nextCommand.label!;
                                }
                                break;
                            }
                        }
                    } else if (nextCommand) {
                        apCommand.nextCommandIdx = nextCommand.index!;
                        apCommand.nextCommandLabel = nextCommand.label!;
                    }
                    break;
                }
                case 'MissionArea': {
                    if (nextCommand) {
                        apCommand.nextCommandIdx = nextCommand.index!;
                        apCommand.nextCommandLabel = nextCommand.label!;
                    }
                    break;
                }
                case 'Land': {
                    break;
                }
            }
        });

        // Add to nextCommandLabelLists and nextCommandIdxList of the ficticious ForkRoute the arrivals and to the initials the ficticious JoinRoute
        apTrajectory.allCommandList?.forEach((apCommand: AutopilotCommand) => {
            if (apCommand.label === 'ForkRouteFicticious') {
                apTrajectory.allCommandList?.forEach((waypoint: AutopilotCommand) => {
                    if (waypoint.stageLabel === 'FirstWaypointArrival') {
                        apCommand.nextCommandIdxList?.push(waypoint.index!);
                        apCommand.nextCommandLabelList?.push(waypoint.label!);
                    }
                });
            }

            if (apCommand.label === 'JoinRouteFicticious') {
                apTrajectory.allCommandList?.forEach((waypoint: AutopilotCommand) => {
                    if (waypoint.stageLabel === 'LastWaypointInitial') {
                        apCommand.prevCommandIdxList?.push(waypoint.index!);
                        waypoint.nextCommandIdx = apCommand.index!;
                        waypoint.nextCommandLabel = apCommand.label!;
                    }
                });
            }
        });

        apTrajectory.allCommandList?.forEach((apCommand: AutopilotCommand) => {
            if (apCommand.branchLabel && apCommand.branchLabel.includes('Initial')) {
                apCommand.stageLabel = 'Departure';
            } else if (apCommand.branchLabel && apCommand.branchLabel.includes('Arrival')) {
                apCommand.stageLabel = 'Arrival';
            } else {
                apCommand.stageLabel = 'Mission';
            }
        });

        apTrajectory.allCommandList?.forEach((apCommand: AutopilotCommand) => {
            if (apCommand.label?.match('Takeoff 0')) {
                apCommand.label = 'DeparturePoint';
            }

            if (apCommand.label?.match('Land 0')) {
                apCommand.label = 'ArrivalPoint';
            }
        });

        apTrajectory.allCommandList?.forEach((apCommand: AutopilotCommand) => {
            if (apCommand.nextCommandLabel?.match('Land 0')) {
                apCommand.nextCommandLabel = 'ArrivalPoint';
            }
        });

        return apTrajectory;
    }
}

/**
 * Definition of the different colors for routes depending on the stage
 */
export class Colors {
    colors: { [key: string]: string } = {
        ['Departure']: '#FF595E',
        ['DeliveryDeparture']: '#FF595E',
        ['Departure-Fork']: '#300204',
        ['Arrival']: '#326CEB',
        ['Land']: '#326CEB',
        ['VertiportArrival']: '#326CEB',
        ['DeliveryArrival']: '#326CEB',
        ['Arrival-Fork']: '#04153b',
        ['Mission']: '#8AC926',
        ['DeliveryRoute']: '#8AC926',
        ['ReturnRoute']: '#8AC926',
        ['Mission-Fork']: '#1c2b03',
        ['Loiter']: '#FFCA3A',
    };

    getColor(stage: string): string {
        return this.colors[stage] ?? '#000000';
    }
}